<template>
  <v-dialog
    ref="dialog"
    max-width="800"
    v-model="showOnboardingModal"
    persistent
  >
    <v-stepper v-model="step">
      <v-stepper-header>
        <v-stepper-step
          :complete="step > 1"
          step="1"
        >
          Datos fiscales
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step
          :complete="step > 2"
          step="2"
        >
          Certificado de sello digital
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step
          :complete="step > 3"
          step="3"
        >
          Datos bancarios
        </v-stepper-step>

        <!-- <v-divider></v-divider>

        <v-stepper-step
          :complete="step > 4"
          step="4"
        >
          Usuarios
        </v-stepper-step> -->
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content step="1">
          <v-card>
          <v-card-text>
            <v-form ref="formStep1" v-model="formStep1.valid">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    data-cy="fiscal-name"
                    dense
                    rounded
                    outlined
                    hide-details
                    :rules="rules.taxName"
                    label="Nombre fiscal"
                    @input="formStep1.form.taxName = formStep1.form.taxName.toUpperCase()"
                    @change="deleteAccents(formStep1.form.taxName)"
                    v-model="formStep1.form.taxName"/>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    data-cy="rfc"
                    dense
                    rounded
                    outlined
                    hide-details
                    :rules="rules.taxId"
                    label="RFC"
                    @input="formStep1.form.taxId = formStep1.form.taxId.toUpperCase()"
                    v-model="formStep1.form.taxId"/>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    data-cy="cp"
                    dense
                    rounded
                    outlined
                    hide-details
                    :rules="rules.zipCode"
                    label="Código postal"
                    maxlength="5"
                    v-model="formStep1.form.address.zipCode"/>
                </v-col>
                <v-col cols="12">
                  <v-autocomplete
                    data-cy="contry-code"
                    v-model="formStep1.form.address.country"
                    label="País"
                    placeholder="MX"
                    :items="countrycodes"
                    :filter="searchCode"
                    item-text="name"
                    item-value="code"
                    dense
                    rounded
                    hide-details
                    outlined
                    :rules="rules.country"/>
                </v-col>
                <v-col cols="12">
                  <v-select
                    data-cy="fiscal-regime"
                    dense
                    rounded
                    outlined
                    hide-details
                    :items="taxRegimes"
                    item-text="name"
                    item-value="satId"
                    :rules="rules.fiscalRegime"
                    label="Regimen Fiscal"
                    v-model="formStep1.form.fiscalRegime">
                    <template v-slot:item="{ item }">
                      {{ item.satId }} | {{ item.name }}
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />

            <v-btn
              data-cy="go-step2"
              color="primary"
              rounded
              depressed
              @click="goStep2"
            >
              Continuar
            </v-btn>
          </v-card-actions>
          </v-card>
        </v-stepper-content>

        <v-stepper-content step="2">
          <v-card>
            <v-card-text>
              <v-form ref="formStep2" v-model="formStep2.valid">
                <v-row>
                  <v-col cols="12">
                    <div class="mb-3">
                      Certificado de sello digital
                    </div>
                    <div>
                      Necesitarás subir el <b>certificado</b> que es el archivo con terminación <b>".cer"</b> y la <b>llave privada</b> que es el archivo con terminación <b>".key"</b> de tu Certificado de Sello Digital.
                    </div>
                    <div>
                      Para más información <a href="https://www.sat.gob.mx/tramites/17507/envia-la-solicitud-para-tu-certificado-de-sello-digital-para-emitir-facturas-electronicas" target="_blank">haz click aquí</a>.
                    </div>
                  </v-col>
                  <v-col cols="12">
                    <v-file-input
                      data-cy="certificate-file"
                      dense
                      rounded
                      outlined
                      hide-details
                      accept=".cer"
                      :rules="rules.certificate"
                      label="Certificado"
                      v-model="formStep2.form.certificateFile"/>
                  </v-col>
                  <v-col cols="12">
                    <v-file-input
                      data-cy="private-key"
                      dense
                      rounded
                      outlined
                      hide-details
                      accept=".key"
                      :rules="rules.privateKey"
                      label="Private key"
                      v-model="formStep2.form.privateKeyFile"/>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      data-cy="password"
                      dense
                      rounded
                      outlined
                      hide-details
                      :rules="rules.privateKeyPassword"
                      :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="showPassword ? 'text' : 'password'"
                      label="Password"
                      v-model="formStep2.form.privateKeyPassword"
                      @click:append="showPassword = !showPassword"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                data-cy="go-step3"
                rounded
                depressed
                @click="skipStep2(true)"
              >
                Saltar
              </v-btn>
              <v-btn
                data-cy="load-fiscalData"
                color="primary"
                rounded
                depressed
                @click="goStep3(false)"
              >
                Cargar datos fiscales
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-content>

        <v-stepper-content step="3">
          <v-card>
            <v-card-text>
              <v-form ref="formStep3" v-model="formStep3.valid">
                <v-row>
                  <v-col cols="12">
                    <v-select
                      data-cy="select-banc"
                      dense
                      rounded
                      outlined
                      hide-details
                      :items="banks"
                      item-text="name"
                      item-value="name"
                      :rules="rules.name"
                      label="Banco"
                      v-model="formStep3.form.name">
                      <template v-slot:item="{ item }">
                        {{ item.name }}
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" v-if="formStep3.form.name === 'hsbc'">
                    <v-text-field
                      data-cy="number"
                      dense
                      rounded
                      outlined
                      hide-details
                      :rules="rules.number"
                      label="Número de cuenta"
                      maxlength="10"
                      v-model="formStep3.form.number"/>
                  </v-col>
                  <v-col cols="12" v-else>
                    <v-text-field
                      data-cy="clabe"
                      dense
                      rounded
                      outlined
                      hide-details
                      :rules="rules.clabe"
                      label="CLABE"
                      maxlength="18"
                      v-model="formStep3.form.clabe"/>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer />

              <v-btn
                data-cy="go-step4"
                color="primary"
                rounded
                depressed
                @click="goStep4"
              >
                Finalizar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-content>

        <!-- <v-stepper-content step="4">
          <v-card>
            <v-card-text>
              <v-form ref="formStep3" v-model="formStep3.valid">
                <v-row>

                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer />

              <v-btn
                color="primary"
                rounded
                depressed
                @click="reset"
              >
                Continue
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-content> -->
      </v-stepper-items>
    </v-stepper>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-dialog>
</template>

<script>
import countrycodes from '@/lib/countrycodes'

export default {
  data () {
    return {
      countrycodes,
      overlay: false,
      step: 1,
      file: null,
      foundProducts: [],
      newProducts: [],
      productsToCreate: [],
      formStep1: {
        valid: false,
        form: {
          taxName: '',
          taxId: '',
          fiscalRegime: '',
          address: {
            zipCode: '',
            country: 'MX'
          }
        }
      },
      formStep2: {
        valid: false,
        form: {
          privateKeyPassword: '',
          certificateFile: null,
          privateKeyFile: null
        }
      },
      formStep3: {
        valid: false,
        form: {
          name: '',
          number: '',
          clabe: ''
        }
      },
      rules: {
        name: [v => !!v || 'Bank is required'],
        number: [
          v => !!v || 'Account number is required',
          v => /^[0-9 ]*$/g.test(v) || 'Only numbers are allowed'
        ],
        clabe: [
          v => !!v || 'CLABE is required',
          v => /^[0-9 ]*$/g.test(v) || 'Only numbers are allowed'
        ],
        zipCode: [
          v => !!v || 'Zipcode is required',
          v => v.length === 5 || 'Zipcode must be 5 digits',
          v => /^[a-zA-Z0-9 ]*$/g.test(v) || 'Only alphabetic characters and numbers are allowed'
        ],
        country: [v => !!v || 'Country is required'],
        certificate: [v => !!v || 'Certificate is required'],
        privateKey: [v => !!v || 'Privatekey is required'],
        privateKeyPassword: [v => !!v || 'Privatekey password is required'],
        taxName: [
          v => !!v || 'Tax name is required',
          v => /^[a-zA-Z0-9 ]*$/g.test(v) || 'Only alphabetic characters and numbers are allowed',
          v => {
            const forbiddenWords = ['SA', 'SAPI', 'CV', 'SRL', 'SNC', 'SCS', 'SCA', 'SAS']
            const regex = new RegExp(`^(?!.*\\b(${forbiddenWords.join('|')})\\b)[a-zA-Z0-9 ]+$`)
            return regex.test(v) || 'The tax name should not contail the fiscal regime'
          }
        ],
        taxId: [
          v => !!v || 'Tax Id is required',
          v => /^[a-zA-Z0-9]*$/g.test(v) || 'Only alphabetic characters and numbers are allowed'
        ],
        fiscalRegime: [v => !!v || 'Fiscal regime is required']
      },
      banks: [
        { name: 'hsbc' },
        { name: 'banamex' },
        { name: 'bancomer' },
        { name: 'santander' },
        { name: 'inbursa' },
        { name: 'banorte' },
        { name: 'azteca' },
        { name: 'scotiabank' },
        { name: 'banregio' },
        { name: 'ixe' },
        { name: 'jpmorgan' },
        { name: 'afirme' },
        { name: 'multiva' },
        { name: 'inmobiliario' },
        { name: 'bancopel' },
        { name: 'banbajio' },
        { name: 'ci' },
        { name: 'mifel' },
        { name: 'citibank' },
        { name: 'stp' },
        { name: 'mercado pago' }
      ],
      taxRegimes: [
        { satId: '601', name: 'General de Ley Personas Morales' },
        { satId: '603', name: 'Personas Morales con Fines no Lucrativos' },
        { satId: '605', name: 'Sueldos y Salarios e Ingresos Asimilados a Salarios' },
        { satId: '606', name: 'Arrendamiento' },
        { satId: '607', name: 'Régimen de Enajenación o Adquisición de Bienes' },
        { satId: '608', name: 'Demás ingresos' },
        { satId: '609', name: 'Consolidación' },
        { satId: '610', name: 'Residentes en el Extranjero sin Establecimiento Permanente en México' },
        { satId: '611', name: 'Ingresos por Dividendos (socios y accionistas)' },
        { satId: '612', name: 'Personas Físicas con Actividades Empresariales y Profesionales' },
        { satId: '614', name: 'Ingresos por intereses' },
        { satId: '615', name: 'Régimen de los ingresos por obtención de premios' },
        { satId: '616', name: 'Sin obligaciones fiscales' },
        { satId: '620', name: 'Sociedades Cooperativas de Producción que optan por diferir sus ingresos' },
        { satId: '621', name: 'Incorporación Fiscal' },
        { satId: '622', name: 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras Sí Sí' },
        { satId: '623', name: 'Opcional para Grupos de Sociedades' },
        { satId: '624', name: 'Coordinados' },
        { satId: '625', name: 'Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
        { satId: '626', name: 'Régimen Simplificado de Confianza' },
        { satId: '628', name: 'Hidrocarburos' },
        { satId: '629', name: 'De los Regímenes Fiscales Preferentes y de las Empresas Multinacionales' },
        { satId: '630', name: 'Enajenación de acciones en bolsa de valores' }
      ],
      showPassword: false
    }
  },
  computed: {
    showOnboardingModal () {
      if (this.$store.state.userProvider) {
        return !this.$store.state.currentProvider.data.onboarded
      }

      return false
    },
    provider () {
      return this.$store.state.currentProvider.data._id
    },
    taxInformation () {
      const taxInformation = this.$store.state.currentProvider.taxInformation.data
      return {
        _id: ((taxInformation || {}).taxInformation || {})._id,
        formStep1: {
          taxName: ((taxInformation || {}).taxInformation || {}).taxName,
          taxId: ((taxInformation || {}).taxInformation || {}).taxId,
          fiscalRegime: ((taxInformation || {}).taxInformation || {}).fiscalRegime,
          address: ((taxInformation || {}).taxInformation || {}).address || {}
        },
        formStep2: {
          privateKeyPassword: ((taxInformation || {}).taxInformation || {}).privateKeyPassword
        },
        formStep3: {
          name: ((taxInformation || {}).bankAccount || {}).name,
          number: ((taxInformation || {}).bankAccount || {}).number,
          clabe: ((taxInformation || {}).bankAccount || {}).clabe
        }
      }
    }
  },
  watch: {
    taxInformation (val) {
      this.formStep1.form = val.formStep1
      this.formStep2.form = val.formStep2
      this.formStep3.form = val.formStep3
    }
  },
  mounted () {
    this.formStep1.form = this.taxInformation.formStep1
    this.formStep2.form = this.taxInformation.formStep2
    this.formStep3.form = this.taxInformation.formStep3
  },
  methods: {
    close () {
      this.reset()
    },
    searchCode (item, queryText, itemText) {
      const name = item.name.toLowerCase()
      const searchText = queryText.toLowerCase()
      return name.indexOf(searchText) >= 0
    },
    reset () {
      if (this.$refs.formStep1) this.$refs.formStep1.resetValidation()
      if (this.$refs.formStep2) this.$refs.formStep2.resetValidation()
      if (this.$refs.formStep3) this.$refs.formStep3.resetValidation()

      this.step = 1
    },
    goStep2 () {
      const valid = this.$refs.formStep1.validate()

      if (valid) {
        this.step = 2
      }
    },
    async skipStep2 (skip) {
      this.overlay = true
      try {
        await this.submitTaxInformation(skip)
        this.step = 3
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    async goStep3 (skip) {
      const valid = this.$refs.formStep2.validate()

      if (valid) {
        this.overlay = true
        try {
          await this.submitTaxInformation(skip)
          this.$toasted.show('Los datos se guardaron con éxito', { duration: 3000 })
          this.step = 3
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    },
    async submitTaxInformation (skip) {
      const form = new FormData()
      form.append('taxName', this.formStep1.form.taxName)
      form.append('taxId', this.formStep1.form.taxId)
      form.append('address', JSON.stringify(this.formStep1.form.address))
      form.append('fiscalRegime', this.formStep1.form.fiscalRegime)

      if (!skip) {
        form.append('certificate', this.formStep2.form.certificateFile)
        form.append('privateKey', this.formStep2.form.privateKeyFile)
        form.append('privateKeyPassword', this.formStep2.form.privateKeyPassword)
      }

      if (!this.taxInformation._id) {
        await this.$api.post(`/v1/providers/${this.provider}/settings`, form)
      } else {
        await this.$api.put(`/v1/providers/${this.provider}/settings`, form)
      }
    },
    async goStep4 () {
      this.overlay = true
      try {
        await this.submitBankInformation()
        await this.finish()
        this.$toasted.show('Los datos se guardaron con éxito', { duration: 3000 })
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    async submitBankInformation () {
      const form = new FormData()
      form.append('bankAccount', JSON.stringify(this.formStep3.form))
      await this.$api.put(`/v1/providers/${this.provider}/settings`, form)
    },
    async finish () {
      await this.$api.post(`/v1/providers/${this.provider}/onboard`)
      await this.$store.dispatch('currentProvider/detail')
    },
    deleteAccents (str) {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    }
  }
}
</script>
